import React, { ReactElement, useEffect, useRef, useState } from "react";
import videojs, { VideoJsPlayer } from "video.js";
import "video.js/dist/video-js.css";
import audioOnlySvg from "../../../../images/audio-only-thumbnail.svg";
import { binSearch } from "../../../../utilities/timestampBinSearch";
import { TimestampsArr, VideoEvent } from "../../../../utilities/useTimestamps";
import { VideoPlaybackAction, VideoPlaybackState } from "../ProctoringResultView.tsx";
import DeletedIcon from "../Resources/deleted-icon.svg?react";
import UnavailableIcon from "../Resources/unavailable-icon.svg?react";
import "./VideoJS.css";

import { PrimitiveAtom } from "jotai";
import i18n from "../../../../../i18n.ts";
import { DetectionOverlay } from "../overlay/DetectionOverlay.tsx";
import {
	useMachineLearningAspectRatio,
	useMachineLearningEventsFromDataDump,
	useMachineLearningNearestDetection,
} from "../overlay/hooks.tsx";
import { VideoSettings } from "../ProctoringVideoPlayer.tsx";
import { VideoDisplayState } from "../store.ts";

export const VideoJS = (props: {
	events: VideoEvent[];
	debugging_string: string | null | undefined;
	dispatch: React.Dispatch<VideoPlaybackAction>;
	options: VideoSettings | null | undefined;
	controls: {
		isPlaying: boolean;
		playbackSpeed: number;
		volume: number;
	};
	startDate: Date;
	arrStamps: TimestampsArr;
	sources: { [key: string]: string };
	displayStateAtom: PrimitiveAtom<VideoDisplayState>;
	videoTimer: VideoPlaybackState;
	toggledTimestamps: boolean;
	isFullscreen: boolean;
	stringExtend: string;
	showingVideoTitles: boolean;
	showAudioThumbnail?: boolean;
	setVideoDuration?: (vidDuration: number) => void;
	isDeleted?: boolean;
	isIncomplete?: boolean;
	videoHasError: boolean;
	setVideoHasError: (value: boolean) => void;
	selectedProctoringSessionUuid?: string;
	showMachineLearningOverlay?: boolean;
}) => {
	//#region Variables
	const {
		debugging_string,
		options,
		controls,
		arrStamps,
		videoTimer,
		startDate,
		dispatch,
		sources,
		isFullscreen,
		showingVideoTitles,
		showAudioThumbnail,
		setVideoDuration,
		isDeleted,
		isIncomplete,
		videoHasError,
		setVideoHasError,
		selectedProctoringSessionUuid,
		showMachineLearningOverlay = false,
	} = props;

	const [playerReady, setPlayerReady] = useState(false);
	const [isInitializing, setIsInitializing] = useState(true);

	/* Global time in seconds, derived from videoTimer */
	const globaltime = videoTimer.timer / 1000;

	/* Reference to the HTML video element */
	const videoRef = useRef<HTMLVideoElement | null>(null);

	const videoContainerRef = useRef<HTMLDivElement | null>(null);

	/* Reference to the Video.js player instance */
	const playerRef = useRef<VideoJsPlayer | null>(null);

	const videoTimestampArr = arrStamps[binSearch(arrStamps, globaltime)];
	const videoSrc = sources[videoTimestampArr.vid];

	/* Error handling state */
	const errorState = useRef<{
		recoveryAttempts: number;
		MAX_RECOVERY_ATTEMPTS: number;
		handlerPromise: Promise<void> | null;
	}>({
		recoveryAttempts: 0,
		MAX_RECOVERY_ATTEMPTS: 5,
		handlerPromise: null,
	});

	useEffect(() => {
		if (videoSrc && videoRef.current) {
			if (!playerRef.current) {
				const player = videojs(videoRef.current, {
					...options,
					sources: [
						{
							src: videoSrc,
							type: "video/webm",
						},
					],
					playbackRates: [0.25, 0.5, 1, 1.25, 1.5, 1.75, 2, 4],
					preload: "auto",
					autoplay: false,
					normalizeAutoplay: false,
					fill: false,
				});
				playerRef.current = player;
				setIsInitializing(true);

				player.on("loadedmetadata", () => {
					/* Update parent video duration */
					if (setVideoDuration) {
						let videoDuration = player.duration();
						if (videoDuration === Infinity) {
							// video's metadata is likely unreadable
							videoDuration = 0;
						}
						setVideoDuration(videoDuration * 1000);
					}
				});

				/**
				 * Disable native error display
				 */
				player.errorDisplay.dispose();

				/**
				 * Error handling callback for the video component
				 */
				const errorHandler = async () => {
					try {
						// Get and verify the error exists
						const error = player.error();
						if (!error) {
							return;
						}

						// If it is not a decode error, hide the player and update the state
						if (error.code !== error.MEDIA_ERR_DECODE) {
							setVideoHasError(true);
							player.hide();
							return;
						} else {
							errorState.current.recoveryAttempts += 1;
						}

						// If it is a decode error, attempt recovery up to the defined max amount of times
						if (errorState.current.recoveryAttempts < errorState.current.MAX_RECOVERY_ATTEMPTS) {
							// Estimate desired video time
							const targetTime = Math.round(
								videoTimestampArr.start ? globaltime - videoTimestampArr.start : player.currentTime(),
							);

							// Attach a callback to wait for the ready event, then reset the player source and state
							const playerReadyPromise = new Promise<void>((resolve) => {
								player.one("ready", () => {
									resolve();
								});
							});

							const currentSrc = player.currentSrc();
							player.pause();
							player.reset();
							player.src(currentSrc);
							player.load();

							await playerReadyPromise;

							// Calculate step size based on recovery attempt
							// Start with larger steps, then get more conservative with each attempt
							const defaultInitialStepSize = 60;
							const stepSize = Math.round(
								Math.min(defaultInitialStepSize, targetTime) / errorState.current.recoveryAttempts,
							);

							// Start from the beginning and seek progressively
							let currentPosition = 0;

							// Keep seeking in steps until we reach the target time
							while (currentPosition < targetTime) {
								// First, seek to the current position
								player.currentTime(currentPosition);

								// Play for a tiny moment to ensure the buffer is loaded
								await player.play();
								player.pause();

								// Calculate next position
								currentPosition = Math.round(currentPosition + stepSize);
							}

							// Restore original playback state
							if (controls.isPlaying) {
								await player.play();
							}

							// Successfully reached near the target position. Resynchronize players using dispatch
							dispatch({ type: "skip", time: videoTimer.timer });
						} else {
							// If max recovery attempts reached, show error state
							setVideoHasError(true);
							player.hide();
						}
					} catch (error) {
						console.error("Error during video recovery:", error);
					}
				};

				/**
				 * On error, wait for the existing handler to finish, then trigger a new one
				 */
				player.on("error", async () => {
					if (errorState.current.handlerPromise) {
						await errorState.current.handlerPromise;
					}
					errorState.current.handlerPromise = errorHandler();
				});
			} else {
				playerRef.current?.src([{ src: videoSrc, type: "video/webm" }]);
				setIsInitializing(true);
			}

			if (!videoHasError && !isDeleted && !isIncomplete) {
				setPlayerReady(false);
				const player = playerRef.current;
				player.one("canplaythrough", () => {
					player.one("timeupdate", () => {
						player.pause();
						setPlayerReady(true);
						dispatch({ type: "skip", time: videoTimer.timer });
					});
					void player.play();
				});
			} else {
				playerRef.current?.pause();
				setPlayerReady(true);
				setIsInitializing(false);
			}
		}
	}, [videoSrc, setVideoHasError, videoHasError, isDeleted, isIncomplete]);

	useEffect(() => {
		if (videoTimer.skip && playerRef.current && playerReady) {
			if (videoTimestampArr && videoTimestampArr.start !== undefined) {
				playerRef.current.currentTime(globaltime - videoTimestampArr.start);
				if (controls.isPlaying) {
					void playerRef.current.play();
				}
				setIsInitializing(false);
				dispatch({ type: "pause" });
			}
		}
	}, [videoTimer.skip, playerRef.current, playerReady, controls.isPlaying]);

	/* Effect to update player volume */
	useEffect(() => {
		playerRef.current?.volume(controls.volume);
	}, [controls.volume]);

	/* Effect to update playback speed */
	useEffect(() => {
		playerRef.current?.playbackRate(controls.playbackSpeed);
	}, [controls.playbackSpeed]);

	/* Effect to handle play/pause state changes */
	useEffect(() => {
		if (controls.isPlaying) {
			void playerRef.current?.play();
		} else {
			playerRef.current?.pause();
		}
	}, [controls.isPlaying]);

	useEffect(() => {
		const videoPlayers = videoContainerRef.current?.querySelectorAll('div[data-vjs-player="true"]');
		videoPlayers?.forEach((element) => {
			if (!(element instanceof HTMLElement)) return;
			if (isDeleted) {
				element.style.display = "none";
			} else {
				element.style.display = "";
			}
		});
	}, [isDeleted]);

	const VideoPlayerUICard = ({ icon, title, text }: { icon: ReactElement; title: string; text: string }) => {
		return (
			<div className="video_thumbnail_wrapper">
				<div className="video_thumbnail_styling">
					<div className="video_thumbnail_icon_wrapper">{icon}</div>
					<p className="video_thumbnail_title">{title}</p>
					<p className="video_thumbnail_text">{text}</p>
				</div>
			</div>
		);
	};

	const shouldHideDefaultVideoPlayerUI = showAudioThumbnail || videoHasError || isInitializing;

	useEffect(() => {
		const player = playerRef.current;
		if (!player) return;

		if (shouldHideDefaultVideoPlayerUI) {
			player.hide();
		} else {
			player.show();
		}
	}, [shouldHideDefaultVideoPlayerUI]);

	const { faceDetections, objectDetections } = useMachineLearningEventsFromDataDump(
		selectedProctoringSessionUuid,
		startDate,
	);
	const { nearestFaceDetection, nearestObjectDetection } = useMachineLearningNearestDetection(
		{ faceDetections, objectDetections },
		globaltime,
	);
	const { aspectRatio } = useMachineLearningAspectRatio(faceDetections, objectDetections);

	const playerDivRef = useRef<HTMLDivElement | null>(null);
	const videoPosition = (() => {
		if (playerDivRef.current) {
			const videoRatio = playerDivRef.current.offsetWidth / playerDivRef.current.offsetHeight;
			if (Number.isNaN(videoRatio)) return;

			// If the video is wider than the aspect ratio, add padding to the left and right
			if (videoRatio > aspectRatio) {
				const excessWidth = playerDivRef.current.offsetWidth - playerDivRef.current.offsetHeight * aspectRatio;
				return {
					top: 0,
					left: excessWidth / 2,
					width: playerDivRef.current.offsetWidth - excessWidth,
					height: playerDivRef.current.offsetHeight,
				};
				// vice versa
			} else {
				const excessHeight = playerDivRef.current.offsetHeight - playerDivRef.current.offsetWidth / aspectRatio;
				return {
					top: excessHeight / 2,
					left: 0,
					width: playerDivRef.current.offsetWidth,
					height: playerDivRef.current.offsetHeight - excessHeight,
				};
			}
		}
	})();

	return (
		<div style={{ height: "100%" }}>
			<div
				className={!isFullscreen ? "video_container" : `video_container_fs`}
				style={
					props.toggledTimestamps
						? {
								paddingLeft: "0",
								paddingRight: "17.9px",
								paddingTop: "26.9px",
								paddingBottom: "26.9px",
								width: "36.75vw",
								height: "20.672vw",
							}
						: {}
				}
				ref={videoContainerRef}
			>
				<div id="vid_title_2" style={showingVideoTitles ? {} : { display: "none" }}>
					<p className="vid_titles">
						{debugging_string == "webcam" ? (showAudioThumbnail ? "Audio" : "Webcam") : "Screen"} Recording
					</p>
				</div>
				{/* {statusImageProps && <StatusImage {...statusImageProps} />} */}
				{showAudioThumbnail && !videoHasError && (
					<div style={{ height: "100%", width: "100%", justifyContent: "center", display: "flex", overflow: "hidden" }}>
						<img style={{ maxWidth: "100%", width: "inherit" }} src={audioOnlySvg} alt="Audio Only" />
					</div>
				)}

				{videoHasError && (
					<VideoPlayerUICard
						icon={isDeleted ? <DeletedIcon /> : <UnavailableIcon />}
						title={
							isDeleted
								? i18n.t("result.components.resultsPage.videoPlayerCard.recordingDeletedTitle")
								: isIncomplete
									? i18n.t("result.components.resultsPage.videoPlayerCard.recordingIncompleteTitle")
									: i18n.t("result.components.resultsPage.videoPlayerCard.recordingErrorTitle")
						}
						text={
							isDeleted
								? i18n.t("result.components.resultsPage.videoPlayerCard.recordingDeletedText")
								: isIncomplete
									? i18n.t("result.components.resultsPage.videoPlayerCard.recordingIncompleteText")
									: i18n.t("result.components.resultsPage.videoPlayerCard.recordingErrorText")
						}
					/>
				)}

				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						flexDirection: "column",
						flexGrow: 1,
					}}
				>
					<div data-vjs-player ref={playerDivRef}>
						<video
							ref={videoRef}
							className="video-js vjs-big-play-centered"
							style={{
								width: "100%",
								maxHeight: "100%",
								objectFit: "contain"
							}}
							onDoubleClick={(e) => e.preventDefault()}
						></video>

						{videoPosition && showMachineLearningOverlay && (
							<div
								style={{
									...videoPosition,
									position: "absolute",
									pointerEvents: "none",
								}}
							>
								{(nearestFaceDetection || nearestObjectDetection) && (
									<DetectionOverlay
										faceDetection={nearestFaceDetection}
										objectDetection={nearestObjectDetection}
										styling={{
											isFullscreen,
										}}
									/>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default VideoJS;
